import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { FaqList } from "@/components/FaqList";
import { CtaBand } from "@/components/CtaBand";
import { CategoryTiles } from "@/components/Tiles";
import { PageHeader } from "@/pages/ContentPage";
import { fetcher, swrOpts } from "@/lib/api";
import { breadcrumbLd, faqLd } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

export function Locations() {
  const { data } = useSWR("/locations", fetcher, swrOpts);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Service Areas", path: "/locations/" }];
  return (
    <>
      <Seo title="Cannabis Delivery Service Areas in Washington, DC | SHADC Dispensary"
        description="See the Washington, DC neighborhoods SHADC Dispensary delivers to — Park View, Petworth, Columbia Heights, Shaw, Capitol Hill, NoMa, Georgetown and more."
        path="/locations/" jsonLd={[breadcrumbLd(crumbs)]} />
      <PageHeader crumbs={crumbs} eyebrow="Delivery coverage" h1="Delivery Service Areas in Washington, DC"
        intro="We deliver from 2906 Georgia Ave NW to residential addresses across the District. Pick a neighborhood for local delivery details." />
      <div className="container-x py-14">
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="locations-grid">
          {(data?.items || []).map((l) => (
            <li key={l.slug}>
              <Link to={`/locations/${l.slug}/`} className={`card-surface group flex h-full flex-col p-6 transition-colors hover:border-gold/60 ${l.hub ? "border-gold/40" : ""}`} data-testid={`location-card-${l.slug}`}>
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">{l.quadrant}</span>
                <span className="mt-2 font-display text-2xl font-semibold text-white group-hover:text-gold">{l.hub ? "All of Washington, DC" : l.name}</span>
                <span className="mt-2 text-sm leading-relaxed text-gray-400">{l.intro}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <CtaBand />
    </>
  );
}

export function LocationPage() {
  const { slug } = useParams();
  const { data: l, error } = useSWR(`/locations/${slug}`, fetcher, swrOpts);
  if (error?.response?.status === 404) return <NotFound />;
  if (!l) return <div className="container-x h-96 animate-pulse py-16" aria-busy="true" />;
  const path = `/locations/${l.slug}/`;
  const crumbs = [{ name: "Home", path: "/" }, { name: "Service Areas", path: "/locations/" }, { name: l.name, path }];
  return (
    <>
      <Seo title={l.title} description={l.meta_description} path={path} jsonLd={[breadcrumbLd(crumbs), faqLd(l.faqs)]} />
      <PageHeader crumbs={crumbs} eyebrow={`${l.quadrant} · Washington, DC`} h1={l.h1} intro={l.intro} testid="location-title" />
      <div className="container-x grid gap-12 py-14 lg:grid-cols-[1.6fr_1fr]">
        <article className="rich max-w-3xl">
          <h2>About delivery in {l.name}</h2>
          {l.about.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}
          <h2>How to order in {l.name}</h2>
          <ol>
            <li>Browse the <Link to="/shop/">menu</Link> and add products to your cart.</li>
            <li>Check out and enter your {l.hub ? "DC" : l.name} address, unit number and access notes.</li>
            <li>Have a valid 21+ photo ID ready when your order arrives.</li>
          </ol>
          <p>Orders over $50 qualify for free delivery. See full <Link to="/delivery/">delivery details</Link>.</p>
          {l.faqs?.length > 0 && <><h2>{l.name} delivery FAQ</h2><div className="mt-4"><FaqList faqs={l.faqs} id="location-faq" /></div></>}
        </article>
        <aside className="space-y-6">
          <Link to={l.guide ? `/blog/${l.guide}/` : "/blog/?category=local-guides"} className="card-surface group block border-gold/40 p-6 hover:border-gold" data-testid="location-guide-link">
            <span className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Local guide</span>
            <span className="mt-2 block text-lg font-semibold text-white group-hover:text-gold">{l.guide ? `Read our ${l.name} delivery guide →` : "Browse all neighborhood guides →"}</span>
          </Link>
          {l.landmarks.length > 0 && (
            <div className="card-surface p-6">
              <h2 className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Around {l.name}</h2>
              <ul className="mt-4 space-y-2 text-gray-300">{l.landmarks.map((m) => <li key={m} className="flex gap-2"><MapPin className="mt-1 h-4 w-4 shrink-0 text-gray-500" aria-hidden />{m}</li>)}</ul>
            </div>
          )}
          <div className="card-surface p-6">
            <h2 className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Nearby areas</h2>
            <ul className="mt-4 flex flex-wrap gap-2">{l.nearby.map((n) => <li key={n.slug}><Link to={`/locations/${n.slug}/`} className="chip" data-testid={`nearby-${n.slug}`}>{n.slug === "dc" ? "All of DC" : n.name}</Link></li>)}</ul>
          </div>
        </aside>
      </div>
      <section className="container-x" aria-labelledby="lc-title"><h2 id="lc-title" className="mb-6 text-3xl font-semibold text-white">Shop for delivery to {l.hub ? "DC" : l.name}</h2><CategoryTiles /></section>
      <CtaBand title={`Order for delivery in ${l.hub ? "Washington, DC" : l.name}`} />
    </>
  );
}
